/**
 * `@dxf-ai-editor/acadctb` — primitive field decoders.
 *
 * AutoCAD writes plot-style values as bare ASCII:
 *   * booleans as `TRUE` / `FALSE` (or `1` / `0`)
 *   * integers as signed decimal
 *   * reals as standard floating point
 *   * colours as packed 32-bit integers (see {@link decodePlotStyleColor})
 *
 * These helpers centralise parsing + range checks so the entry
 * builder stays declarative. All numeric parsers fall back to the
 * supplied default when the leaf is missing, but raise
 * `ParseAcadctbError` with `code = "field"` on malformed input.
 */

import { makeError } from "./errors";
import type { PlotStyleColor } from "./types";

export function parseBool(
  value: string | undefined,
  fallback: boolean,
): boolean {
  if (value === undefined) {
    return fallback;
  }
  const upper = value.trim().toUpperCase();
  if (upper === "TRUE" || upper === "1") {
    return true;
  }
  if (upper === "FALSE" || upper === "0") {
    return false;
  }
  throw makeError("field", `expected boolean, got '${value}'`);
}

export function parseIntField(
  value: string | undefined,
  fallback: number,
  fieldName: string,
): number {
  if (value === undefined) {
    return fallback;
  }
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    throw makeError(
      "field",
      `field '${fieldName}' is not a valid integer: '${value}'`,
    );
  }
  return Number.parseInt(trimmed, 10);
}

export function parseUint32Field(
  value: string | undefined,
  fallback: number,
  fieldName: string,
): number {
  const signed = parseIntField(value, fallback, fieldName);
  // Reinterpret as unsigned 32-bit.
  return signed >>> 0;
}

export function parseFloatField(
  value: string | undefined,
  fallback: number,
  fieldName: string,
): number {
  if (value === undefined) {
    return fallback;
  }
  const trimmed = value.trim();
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n)) {
    throw makeError(
      "field",
      `field '${fieldName}' is not a valid real: '${value}'`,
    );
  }
  return n;
}

export function parseStringField(
  value: string | undefined,
  fallback: string,
): string {
  if (value === undefined) {
    return fallback;
  }
  // AutoCAD writes the description as-is; no quoting conventions to
  // unwrap. We trim trailing whitespace to normalise CRLF artefacts.
  return value.trimEnd();
}

/**
 * Decode a packed AutoCAD plot-style colour word.
 *
 * Encoding:
 *
 *   * raw `-1` (or unsigned `0xFFFFFFFF`) → use object colour
 *   * high byte `0xC0` → BYBLOCK (use object colour)
 *   * high byte `0xC1` → BYLAYER (use object colour)
 *   * high byte `0xC2` → explicit RGB in lower 24 bits
 *   * anything else → defensive fallback to "use object colour"
 *     (matches ezdxf's behaviour for unknown high bytes).
 */
export function decodePlotStyleColor(packed: number): PlotStyleColor {
  const u = packed >>> 0;
  if (u === 0xffffffff) {
    return { useObject: true, r: 0, g: 0, b: 0 };
  }
  const highByte = (u >>> 24) & 0xff;
  if (highByte === 0xc2) {
    const r = (u >>> 16) & 0xff;
    const g = (u >>> 8) & 0xff;
    const b = u & 0xff;
    return { useObject: false, r, g, b };
  }
  // 0xC0 (BYBLOCK), 0xC1 (BYLAYER), or unrecognised — fall back to
  // "use entity's own colour".
  return { useObject: true, r: 0, g: 0, b: 0 };
}
